<script setup lang="ts">
import {inject, ref, watch} from 'vue'
import * as yup from 'yup'
import {useField, useForm} from 'vee-validate'
import {PortalAuthKey} from "../plugins/auth.ts"
import User from "../models/User.ts"
import AuthLayout from "../layouts/AuthLayout.vue"
import ProfilePhotoField from '../components/ProfilePhotoField.vue'
import UserAvatar from "../components/UserAvatar.vue";
import PhoneNumberInput from "../components/PhoneNumberInput.vue";
import {useMessagesStore} from "../stores/messages.ts";
import EmailField from "../components/EmailField.vue";

const auth = inject(PortalAuthKey)
const user = ref<User>(User.clone(auth.authState.user))

const messages = useMessagesStore();

// Yup validation schema
const schema = yup.object({
  username: yup.string().required().label('Username'),
  first_name: yup.string().required().label('First Name'),
  last_name: yup.string().required().label('Last Name'),
  email: yup.string().email().required().label('Email'),
  phone: yup.string().required().label('Phone')
      .matches(/^\+[1-9]{1,2}[0-9]{3}[0-9]{3}([0-9]{4})$/gm, "Phone number must be a valid phone number"),
  user_icon: yup.mixed<File>().nullable().label('Profile Photo'),
})

// Initialize Vee-Validate form
const {handleSubmit} = useForm({
  validationSchema: schema,
  initialValues: {
    username: user.value.username,
    first_name: user.value.first_name,
    last_name: user.value.last_name,
    email: user.value.email,
    phone: user.value.phone,
    user_icon: null
  }
})

const emailFieldRef = ref<InstanceType<typeof EmailField>>()

// Define each field
const username = useField<string>('username')
const first_name = useField<string>('first_name')
const last_name = useField<string>('last_name')
const email = useField<string>('email')
const phone = useField<string>('phone')
const user_icon = useField<File | null>('user_icon')

// State for loading and alerts
const loadingUpdateProfile = ref(false)
const updateErrorAlert = ref(false)
const updateSuccess = ref(false)

// Submit handler
const onSubmit = handleSubmit(async (values) => {
  // Block submission if email is not available
  const emailIsAvailable =
      emailFieldRef.value?.emailAvailable === true ||
      email.value.value.toLowerCase() === user.value?.email?.toLowerCase()

  if (!emailIsAvailable) {
    return;
  }

  loadingUpdateProfile.value = true

  const [success, _, error] = await user.value.update({
    ...user.value,
    username: values.username,
    first_name: values.first_name,
    last_name: values.last_name,
    email: values.email,
    phone: values.phone
  });

  if (!success) {
    console.error(error)
    loadingUpdateProfile.value = false
    updateErrorAlert.value = true
    return
  }

  // Upload the cropped profile picture if it exists
  if (values.user_icon) {
    const formData = new FormData()
    formData.append('file', values.user_icon)

    const [photoSuccess, __, photoError] = await user.value.updateProfilePicture(formData)
    if (!photoSuccess) {
      console.error(photoError)
    }

    // Optionally reset the user_icon field if you want to clear it
    user_icon.resetField()
    user.value = User.clone(auth.authState.user)
  }

  // Refresh user data
  await auth.checkAuthStatus()
  user.value = auth.authState.user

  loadingUpdateProfile.value = false
  updateSuccess.value = true
})

const loadingDisableUserAccount = ref(false)
const disableUserAccountAlert = ref(false);
const disableUserAccount = async () => {
  loadingDisableUserAccount.value = true
  await user.value.deactivate(true).then(([success, error]) => {
    if (!success) {
      console.error(error)
      messages.add({
        text: 'Failed to deactivate account.',
        color: 'red',
      })
    } else {
      messages.add({
        text: 'Account deactivated.',
        color: 'success',
      })
      auth.logout()
    }
  }).finally(() => {
    loadingDisableUserAccount.value = false
  })
}


// --- Two-Factor Auth toggle logic
const twoFactorEnabled = ref<boolean>(false); // initial value from user data or API
twoFactorEnabled.value = user.value.two_factor_enabled;

watch(twoFactorEnabled, async (newValue, oldValue) => {
  try {
    if (newValue === true) {
      await user.value.enableTwoFactor().then(([success, error]) => {
        if (success) {
          messages.add({
            text: 'Two-factor authentication enabled.',
            color: 'success',
          });
        } else {
          throw error;
        }
      });
    } else {
      await user.value.disableTwoFactor().then(([success, error]) => {
        if (success) {
          messages.add({
            text: 'Two-factor authentication disabled.',
            color: 'success',
          });
        } else {
          throw error;
        }
      })
    }
  } catch (err) {
    twoFactorEnabled.value = oldValue;

    messages.add({
      text: 'Failed to update two-factor setting.',
      color: 'red',
    });
  }
}, {immediate: false});

// ---
</script>


<template>
  <AuthLayout>
    <v-container class="d-flex flex-column align-center">
      <v-card width="600">
        <v-toolbar title="My Account" density="compact" class="text-center">
          <template #prepend>
            <v-tooltip text="Back to Landing">
              <template v-slot:activator="{ props }">
                <v-btn icon v-bind="props" to="/landing">
                  <v-icon>mdi-arrow-left</v-icon>
                </v-btn>
              </template>
            </v-tooltip>
          </template>
          <template #append>
            <v-tooltip text="Save Changes">
              <template v-slot:activator="{ props }">
                <v-btn icon v-bind="props" @click="onSubmit">
                  <v-icon>mdi-content-save</v-icon>
                </v-btn>
              </template>
            </v-tooltip>
          </template>
        </v-toolbar>
        <v-card-text>
          <v-card variant="flat" class="pa-6 text-center">
            <div class="d-flex flex-column align-center justify-center mb-4">
              <UserAvatar :user="user as User" size="128" class="elevation-3 mb-3"/>
              <div class="text-h6">{{ user.fullName || user.username }}</div>
              <div class="text-body-2 text-medium-emphasis">{{ user.email }}</div>
            </div>

            <v-divider class="my-4"/>

            <h2 class="text-subtitle-1 font-weight-medium mb-3 text-left">Update Profile Photo</h2>
            <ProfilePhotoField name="user_icon" v-model="user_icon.value.value"/>
          </v-card>
          <v-card variant="flat">
            <v-toolbar title="Personal Information" density="compact">
              <template #prepend>
                <v-icon class="mx-2">mdi-account</v-icon>
              </template>
            </v-toolbar>
            <v-card-text>
              <v-text-field class="my-2" label="Username*" disabled v-model="username.value.value"
                            :error-messages="username.errorMessage.value">
              </v-text-field>
              <v-text-field :disabled="loadingUpdateProfile" :loading="loadingUpdateProfile" class="my-2"
                            label="First Name*" v-model="first_name.value.value"
                            :error-messages="first_name.errorMessage.value"></v-text-field>
              <v-text-field :disabled="loadingUpdateProfile" :loading="loadingUpdateProfile" class="my-2"
                            label="Last Name*" v-model="last_name.value.value"
                            :error-messages="last_name.errorMessage.value"></v-text-field>
              <EmailField :current-email="user.email" name="email" v-model="email.value.value"/>
              <PhoneNumberInput
                  v-model="phone.value.value"
                  label="Mobile Phone*"
                  hint="Formats like (801) 555-1234 are accepted"
                  country="US"
              />
            </v-card-text>
          </v-card>
          <v-card variant="flat">
            <v-toolbar title="Security Settings" density="compact">
              <template #prepend>
                <v-icon class="mx-2">mdi-lock</v-icon>
              </template>
            </v-toolbar>
            <v-card-text class="d-flex flex-column gap-4">
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="text-body-1 font-weight-medium">Change Password</div>
                  <div class="text-caption text-grey">Update your login credentials to keep your account secure.</div>
                </div>
                <v-btn
                    color="primary"
                    variant="outlined"
                    to="/changepass"

                    class="ml-2"
                    :aria-label="'Change Password'"
                >
                  <v-icon>mdi-key-change</v-icon>
                  Change
                </v-btn>
              </div>

              <v-divider class="my-2"></v-divider>

              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="text-body-1 font-weight-medium">Two-Factor Authentication</div>
                  <div class="text-caption text-grey">Adds an extra layer of security to your account.</div>
                </div>
                <v-switch
                    :disabled="user.type === 'user'"
                    inset
                    hide-details
                    color="primary"
                    v-model="twoFactorEnabled"
                />
              </div>
              <div v-if="user.type === 'user'" class="text-caption text-warning mt-1">
                System users are required to use two-factor-authentication
              </div>

              <v-divider class="my-2"/>

              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="text-body-1 font-weight-medium">Temporarily Disable Account</div>
                  <div class="text-caption text-grey">
                    In case of a suspected account compromise, you can disable this account.
                  </div>
                </div>
                <v-btn
                    color="error"
                    variant="outlined"
                    class="ml-2"
                    @click="disableUserAccountAlert = true"
                    :loading="loadingDisableUserAccount"
                    :aria-label="'Temporarily Disable Account'"
                >
                  <v-icon>mdi-account-off</v-icon>
                  Disable
                </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </v-card-text>
      </v-card>
    </v-container>
  </AuthLayout>
  <v-dialog v-model="disableUserAccountAlert" max-width="520" persistent>
    <v-card>
      <v-toolbar density="compact" color="warning" title="Disable Account" />
      <v-card-text class="pt-4">
        <div class="mb-2">
          This will immediately disable your account and sign you out.
        </div>
        <div class="text-body-2 text-medium-emphasis">
          Use this only if you believe the account has been compromised. You will not be able to sign back in unless a systems administrator reactivates it.
        </div>
      </v-card-text>
      <v-card-actions class="px-6 pb-4">
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="disableUserAccountAlert = false" :disabled="loadingDisableUserAccount">Cancel</v-btn>
        <v-btn @click="disableUserAccount" color="warning" :loading="loadingDisableUserAccount">Continue</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  <v-snackbar v-model="updateErrorAlert" color="error">
    There was an error updating your profile

    <template v-slot:actions>
      <v-btn color="white" variant="text" @click="updateErrorAlert = false">
        Close
      </v-btn>
    </template>
  </v-snackbar>
  <v-snackbar v-model="updateSuccess" color="success">
    Profile updated successfully

    <template v-slot:actions>
      <v-btn color="white" variant="text" @click="updateSuccess = false">
        Close
      </v-btn>
    </template>
  </v-snackbar>
</template>
